import type { z } from "zod/v4";
import type { ClientApiFeaturePathParams, TenantPathParams } from "./index";

// ==================== Scope ====================

/**
 * Resource scope determines URL structure:
 * - "camera" (default): /v2/tenants/{tenant}/features/{feature}/sites/{site}/cameras/{camera}/{resource}
 * - "tenant": /v2/tenants/{tenant}/features/{feature}/{resource}
 */
export type ResourceScope = "camera" | "tenant";

// ==================== Path Params ====================

// Camera-scoped path params (default) - includes site/camera with wildcard support
export type CameraPathParams = z.output<typeof ClientApiFeaturePathParams>;

// Tenant-scoped path params - just tenantId
export type TenantOnlyPathParams = z.output<typeof TenantPathParams>;

// Base path params for general use (camera scope, for backwards compatibility)
export type BasePathParams = CameraPathParams;

// ==================== Type Utilities ====================

/**
 * Type utility - extracts parameter names from a URI template.
 * e.g., "snapshots/{snapshotId}/versions/{versionId}" -> "snapshotId" | "versionId"
 */
type ExtractParams<T extends string> =
  T extends `${string}{${infer Param}}${infer Rest}` ? Param | ExtractParams<Rest> : never;

/**
 * Type utility - maps extracted params to a record of strings.
 */
export type ResourceParams<T extends string> = {
  [K in ExtractParams<T>]: string;
};

/**
 * Type utility - checks if a URI template has parameters.
 */
export type HasResourceParams<T extends string> = keyof ResourceParams<T> extends never ? false : true;

// ==================== Wire Types ====================

/**
 * Wire type for query params - must encode to string values (or arrays of strings).
 * This constraint ensures schemas passed to resource definitions have
 * wire types compatible with URLSearchParams.
 */
export type QueryParamsWire = Record<string, string | string[] | undefined>;

/**
 * Type constraint for query schemas - wire type must be QueryParamsWire.
 * This ensures schemas encode to URL-compatible string values.
 */
export type QuerySchema = z.ZodType<unknown, QueryParamsWire>;

// ==================== Resource Config ====================

/**
 * Internal base resource config (without conditional resourcePathSchema).
 */
interface ResourceConfigFields<TResource extends string, TScope extends ResourceScope = "camera"> {
  feature: string;
  resource: TResource;
  /**
   * Scope determines URL structure:
   * - "camera" (default): includes /sites/{site}/cameras/{camera}/ in path
   * - "tenant": feature-only path without site/camera segments
   */
  scope?: TScope;
}

/**
 * Resource config - defines the URL/path structure for a resource.
 * Conditionally requires resourcePathSchema based on resource template.
 *
 * This is the base config for both queries and mutations.
 * Query/mutation adapters add their own schemas (responseSchema, bodySchema, querySchema).
 */
export type ResourceConfig<
  TResource extends string,
  TResourcePathSchema extends z.ZodType<unknown, ResourceParams<TResource>>,
  TScope extends ResourceScope = "camera",
> =
  HasResourceParams<TResource> extends true ?
    ResourceConfigFields<TResource, TScope> & { resourcePathSchema: TResourcePathSchema }
  : ResourceConfigFields<TResource, TScope> & {
      resourcePathSchema?: TResourcePathSchema | undefined;
    };

/**
 * Simplified resource config type for use in CDK and other contexts
 * that don't need full type parameters.
 */
export type AnyResourceConfig = ResourceConfig<string, z.ZodType<unknown, Record<string, string>>, ResourceScope>;

// ==================== defineResource ====================

/**
 * Defines a client API resource - the single source of truth for both
 * client-side React Query hooks and server-side CDK routing.
 *
 * Per ADR 0031, "resource" is the path segment after feature (and site/camera for camera scope).
 *
 * @example
 * // Camera-scoped (default)
 * const snapshotsResource = defineResource({
 *   feature: "media",
 *   resource: "snapshots",
 * });
 * // URL: /v2/tenants/{tenant}/features/media/sites/{site}/cameras/{camera}/snapshots
 *
 * // Tenant-scoped
 * const rolesResource = defineResource({
 *   feature: "access",
 *   resource: "roles",
 *   scope: "tenant",
 * });
 * // URL: /v2/tenants/{tenant}/features/access/roles
 *
 * // Client: queryFor(defineEndpoint(snapshotsResource, { responseSchema }))
 */
export function defineResource<
  TResource extends string,
  TResourcePathSchema extends z.ZodType<unknown, ResourceParams<TResource>>,
  TScope extends ResourceScope = "camera",
>(config: ResourceConfig<TResource, TResourcePathSchema, TScope>) {
  return config;
}

// ==================== Helpers for URL building ====================

/**
 * Interpolates a URI template with values from params object.
 * e.g., "snapshots/{snapshotId}" + { snapshotId: "abc" } -> "snapshots/abc"
 */
export function interpolateResource(template: string, params: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => {
    const value = params[key];
    if (value === undefined) {
      throw new Error(`Missing URI template param: ${key}`);
    }
    return encodeURIComponent(value);
  });
}

/**
 * Converts a wire-format query object to URLSearchParams string.
 * Handles arrays as repeated params (OR semantics per API design).
 * No type casting needed - QueryParamsWire guarantees compatible types.
 */
export function queryToSearchParams(wire: QueryParamsWire): string {
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(wire)) {
    if (v === undefined) continue;
    if (Array.isArray(v)) {
      for (const item of v) params.append(k, item);
    } else {
      params.set(k, v);
    }
  }
  return params.toString();
}
